import React, { useEffect } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../features/auth/authSlice.js";
import { fetchUnreadMessages } from "../features/chats/chatThunks.js";
import { useSidebar } from "../pages/SidebarContext.jsx";
import Logo from "../assets/logo-tintore.png";

import {
  Calendar,
  MessageSquare,
  Users,
  Settings,
  Bot,
  LogOut,
  User as UserIcon,
  Briefcase,
  Clock,
  BarChart3,
} from "lucide-react";

import "../styles/sidebar.css";

const Sidebar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isSidebarOpen, closeSidebarOnMobile } = useSidebar();

  const { user, token } = useSelector((state) => state.auth || {});
  const totalUnread = useSelector((state) => state.chat?.totalUnread || 0);

  const userRole = (user?.role || "").toLowerCase();
  const isPatient = userRole === "patient";
  const isAdmin = userRole === "admin";
  const isReceptionist = userRole === "receptionist";

  const canSeeCrm = isAdmin || isReceptionist;

  useEffect(() => {
    if (!token || isPatient) return;
    dispatch(fetchUnreadMessages());
  }, [dispatch, token, isPatient]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/log-in");
    closeSidebarOnMobile();
  };

  const displayName =
    user?.first_name ||
    user?.name ||
    user?.username ||
    (user?.email ? user.email.split("@")[0] : "Invitado");

  const avatarUrl = user?.profile_picture || user?.profilePicture || null;

  const navLinkClass = ({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`;

  let userRoleLabel = "Usuario";
  if (isAdmin) userRoleLabel = "Administrador";
  else if (userRole === "doctor") userRoleLabel = "Profesional de la salud";
  else if (isPatient) userRoleLabel = "Paciente";
  else if (isReceptionist) userRoleLabel = "Administrativo";

  const handleNavClick = () => closeSidebarOnMobile();

  return (
    <aside className={`sidebar ${isSidebarOpen ? "sidebar--open" : ""}`}>
      <div className="sidebar-brand">
        <img src={Logo} alt="Clínica Tintoré" className="sidebar-logo" />
      </div>

      <nav className="sidebar-menu">
        <ul>
          <li>
            <NavLink to="/appointments" className={navLinkClass} onClick={handleNavClick}>
              <Calendar className="sidebar-icon" aria-hidden="true" />
              <span>Calendario</span>
            </NavLink>
          </li>

          {!isPatient && (
            <li>
              <NavLink to="/chats" className={navLinkClass} onClick={handleNavClick}>
                <MessageSquare className="sidebar-icon" aria-hidden="true" />
                <span>Chats</span>
                {totalUnread > 0 && (
                  <span className="sidebar-badge">{totalUnread > 99 ? "99+" : totalUnread}</span>
                )}
              </NavLink>
            </li>
          )}

          {canSeeCrm && (
            <li>
              <NavLink to="/crm" className={navLinkClass} onClick={handleNavClick}>
                <Briefcase className="sidebar-icon" aria-hidden="true" />
                <span>CRM</span>
              </NavLink>
            </li>
          )}

          {!isPatient && (
            <li>
              <NavLink to="/hr/employee" className={navLinkClass} onClick={handleNavClick}>
                <Clock className="sidebar-icon" aria-hidden="true" />
                <span>Mi jornada</span>
              </NavLink>
            </li>
          )}

          {isAdmin && (
            <li>
              <NavLink to="/hr/manager" className={navLinkClass} onClick={handleNavClick}>
                <BarChart3 className="sidebar-icon" aria-hidden="true" />
                <span>Panel manager</span>
              </NavLink>
            </li>
          )}

          <li>
            <NavLink to="/patients" className={navLinkClass} onClick={handleNavClick}>
              <Users className="sidebar-icon" aria-hidden="true" />
              <span>{isPatient ? "Paciente" : "Pacientes"}</span>
            </NavLink>
          </li>

          <li>
            <NavLink to="/assistant" className={navLinkClass} onClick={handleNavClick}>
              <Bot className="sidebar-icon" aria-hidden="true" />
              <span>Asistente</span>
            </NavLink>
          </li>

          <li>
            <NavLink to="/profile" className={navLinkClass} onClick={handleNavClick}>
              <Settings className="sidebar-icon" aria-hidden="true" />
              <span>Ajustes</span>
            </NavLink>
          </li>
        </ul>
      </nav>

      <div className="sidebar-footer">
        {token ? (
          <div className="sidebar-user-card">
            <div className="sidebar-user-info">
              <div className="sidebar-avatar">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Tu foto de perfil" />
                ) : (
                  <span>{displayName.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="sidebar-user-text">
                <p className="sidebar-user-name">{displayName}</p>
                <p className="sidebar-user-role">{userRoleLabel}</p>
              </div>
            </div>

            <button type="button" onClick={handleLogout} className="sidebar-logout-btn">
              <LogOut className="sidebar-logout-icon" aria-hidden="true" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        ) : (
          <div className="sidebar-auth-buttons">
            <button
              type="button"
              onClick={() => {
                navigate("/log-in");
                closeSidebarOnMobile();
              }}
              className="sidebar-auth-btn primary"
            >
              <UserIcon className="sidebar-auth-icon" aria-hidden="true" />
              <span>Log in</span>
            </button>
            <button
              type="button"
              onClick={() => {
                navigate("/sign-up");
                closeSidebarOnMobile();
              }}
              className="sidebar-auth-btn ghost"
            >
              Crear cuenta
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;